import apiClient from "./apiClient";

// List (with pagination + search + filters)
export const fetchMasterData = async (type, { page = 1, page_size = 10, search = "", state_id, district_id, is_hub, category, has_assessment, cycle_id, cycle_type, include_deleted, deleted_only } = {}) => {
 const params = { type, page, page_size, search };
 if (state_id) params.state_id = state_id;
 if (district_id) params.district_id = district_id;
 if (is_hub !== undefined && is_hub !== null && is_hub !== "") params.is_hub = is_hub;
 if (category) params.category = category;
 if (has_assessment !== undefined && has_assessment !== null) params.has_assessment = has_assessment;
 if (cycle_id) params.cycle_id = cycle_id;
 if (cycle_type) params.cycle_type = cycle_type;
 if (include_deleted !== undefined && include_deleted !== null && include_deleted !== "") params.include_deleted = include_deleted;
 if (deleted_only !== undefined && deleted_only !== null && deleted_only !== "") params.deleted_only = deleted_only;
 const response = await apiClient.get(`/master/commons/list`, { params });
 return response.data;
};

// States
export const fetchStateById = async (id) => {
 const response = await apiClient.get(`/master/states/${id}`);
 return response.data;
};

export const createState = async (name) => {
 const response = await apiClient.post(`/master/states/create`, { name });
 return response.data;
};

export const updateState = async (id, name) => {
 const response = await apiClient.put(`/master/states/${id}`, { name });
 return response.data;
};

export const deleteState = async (id) => {
 const response = await apiClient.delete(`/master/states/${id}`);
 return response.data;
};

// Restore a soft-deleted state (ADMIN only, enforced server-side)
export const restoreState = async (id) => {
 const response = await apiClient.post(`/master/states/${id}/restore`);
 return response.data;
};

// Districts
export const fetchDistrictById = async (id) => {
 const response = await apiClient.get(`/master/districts/${id}`);
 return response.data;
};

export const createDistrict = async (name, state_id, is_hub = false) => {
 const response = await apiClient.post(`/master/districts/create`, {
 name,
 state_id,
 is_hub,
 });
 return response.data;
};

export const updateDistrict = async (id, name, state_id, is_hub) => {
 const body = { name, state_id };
 if (is_hub !== undefined) body.is_hub = is_hub;
 const response = await apiClient.put(`/master/districts/${id}`, body);
 return response.data;
};

export const deleteDistrict = async (id) => {
 const response = await apiClient.delete(`/master/districts/${id}`);
 return response.data;
};

// Restore a soft-deleted district (ADMIN only, enforced server-side)
export const restoreDistrict = async (id) => {
 const response = await apiClient.post(`/master/districts/${id}/restore`);
 return response.data;
};

// Blocks
export const fetchBlockById = async (id) => {
 const response = await apiClient.get(`/master/blocks/${id}`);
 return response.data;
};

export const createBlock = async (name, district_id, is_accelerated = false) => {
 const body = { name, district_id, is_accelerated };
 const response = await apiClient.post(`/master/blocks/create`, body);
 return response.data;
};

export const updateBlock = async (id, name, district_id, is_accelerated) => {
 const body = { name, district_id };
 if (is_accelerated !== undefined) body.is_accelerated = is_accelerated;
 const response = await apiClient.put(`/master/blocks/${id}`, body);
 return response.data;
};

export const deleteBlock = async (id) => {
 const response = await apiClient.delete(`/master/blocks/${id}`);
 return response.data;
};

// Restore a soft-deleted block (ADMIN only, enforced server-side)
export const restoreBlock = async (id) => {
 const response = await apiClient.post(`/master/blocks/${id}/restore`);
 return response.data;
};

// Bulk Actions
export const bulkAction = async (entityType, action, ids) => {
 const response = await apiClient.post(`/master/${entityType}/bulk-action`, {
 action,
 ids,
 });
 return response.data;
};

// Bulk Upload
export const bulkUpload = async (entityType, file) => {
 const formData = new FormData();
 formData.append("file", file);
 // Schools use /school prefix; master-data entities use /master
 const prefix = entityType === "schools" ? "/school" : "/master";
 const response = await apiClient.post(
 `${prefix}/${entityType}/bulk-upload`,
 formData,
 { headers: { "Content-Type": "multipart/form-data" } }
 );
 return response.data;
};

// Bulk upload MCQ sets together with their questions, from one workbook holding
// a "Sets" sheet and a "Questions" sheet joined on (competency_code, set_title).
// Atomic across both sheets. Leaving "Sets" empty adds questions to sets that
// already exist.
export const bulkUploadMcqSet = async (file) => {
 const formData = new FormData();
 formData.append("file", file);
 const response = await apiClient.post(
 `/master/competency-sets/bulk-upload-mcq-set`,
 formData,
 { headers: { "Content-Type": "multipart/form-data" } }
 );
 return response.data;
};

// Questions-only variant, kept for API callers. The UI uses bulkUploadMcqSet,
// which accepts the same "Questions" sheet.
export const bulkUploadSetQuestions = async (file) => {
 const formData = new FormData();
 formData.append("file", file);
 const response = await apiClient.post(
 `/master/competency-sets/bulk-upload-questions`,
 formData,
 { headers: { "Content-Type": "multipart/form-data" } }
 );
 return response.data;
};

// Schools (separate service at /school, with pagination + search + filters)
export const fetchSchools = async ({ page = 1, page_size = 10, search = "", block_id, district_id, state_id, is_adopted, is_active, has_assessment, cycle_id, cycle_type, include_deleted, deleted_only, in_cycle_id, in_cycle_only } = {}) => {
 const params = { page, page_size, search };
 // in_cycle_id tags each row with in_cycle; in_cycle_only keeps only that cycle's schools
 if (in_cycle_id) params.in_cycle_id = in_cycle_id;
 if (in_cycle_only) params.in_cycle_only = true;
 if (block_id) params.block_id = block_id;
 if (district_id) params.district_id = district_id;
 if (state_id) params.state_id = state_id;
 if (is_adopted !== undefined && is_adopted !== null && is_adopted !== "") params.is_adopted = is_adopted;
 if (is_active !== undefined && is_active !== null && is_active !== "") params.is_active = is_active;
 if (has_assessment !== undefined && has_assessment !== null) params.has_assessment = has_assessment;
 if (cycle_id) params.cycle_id = cycle_id;
 if (cycle_type) params.cycle_type = cycle_type;
 if (include_deleted !== undefined && include_deleted !== null && include_deleted !== "") params.include_deleted = include_deleted;
 if (deleted_only !== undefined && deleted_only !== null && deleted_only !== "") params.deleted_only = deleted_only;
 const response = await apiClient.get(`/school/schools/fetch`, { params });
 return response.data;
};

export const fetchSchoolById = async (id) => {
 const response = await apiClient.get(`/school/schools/fetch/${id}`);
 return response.data;
};

export const createSchool = async (data) => {
 const response = await apiClient.post(`/school/schools/create`, data);
 return response.data;
};

export const updateSchool = async (id, data) => {
 const response = await apiClient.put(`/school/schools/update/${id}`, data);
 return response.data;
};

export const deleteSchool = async (id) => {
 const response = await apiClient.delete(`/school/schools/delete/${id}`);
 return response.data;
};

export const bulkActionSchools = async (action, ids) => {
 const response = await apiClient.post(`/school/schools/bulk-action`, {
 action,
 ids,
 });
 return response.data;
};

// Restore a soft-deleted school (ADMIN only, enforced server-side)
export const restoreSchool = async (id) => {
 const response = await apiClient.post(`/school/schools/restore/${id}`);
 return response.data;
};

// ── Students (separate service at /school) ─────────────────────────────

// List students. Note: unlike other list endpoints this one does NOT
// support `state_id` — only `school_id` / `grade` / `search` filters,
// per services/school/app/api/students.py.
export const fetchStudents = async ({ page = 1, page_size = 20, school_id, grade, search, include_deleted, deleted_only } = {}) => {
 const params = { page, page_size };
 if (school_id) params.school_id = school_id;
 if (grade) params.grade = grade;
 if (search) params.search = search;
 if (include_deleted !== undefined && include_deleted !== null && include_deleted !== "") params.include_deleted = include_deleted;
 if (deleted_only !== undefined && deleted_only !== null && deleted_only !== "") params.deleted_only = deleted_only;
 const response = await apiClient.get(`/school/students/`, { params });
 return response.data;
};

// Restore a soft-deleted student (ADMIN only, enforced server-side)
export const restoreStudent = async (id) => {
 const response = await apiClient.post(`/school/students/${id}/restore`);
 return response.data;
};

// Upload non-adopted schools for Quarterly Cycle (same template as bulk-upload)
export const uploadNonAdoptedSchools = async (file) => {
 const formData = new FormData();
 formData.append("file", file);
 const response = await apiClient.post(
 `/school/schools/bulk-upload-non-adopted`,
 formData,
 { headers: { "Content-Type": "multipart/form-data" } }
 );
 return response.data;
};

// Preview non-adopted schools upload (validate-only, NO DB writes)
export const previewNonAdoptedSchools = async (file, stateId, cycleId) => {
 const formData = new FormData();
 formData.append("file", file);
 if (stateId) formData.append("state_id", stateId);
 if (cycleId) formData.append("cycle_id", cycleId);
 const response = await apiClient.post(
 `/school/schools/preview-non-adopted`,
 formData,
 { headers: { "Content-Type": "multipart/form-data" } }
 );
 return response.data;
};

// Batch-create non-adopted schools from validated data (called on cycle submit)
export const createNonAdoptedBatch = async (schools, stateId) => {
 const response = await apiClient.post(
 `/school/schools/create-non-adopted-batch`,
 { schools, ...(stateId ? { state_id: stateId } : {}) }
 );
 return response.data;
};


// ── Assessment Years ─────────────────────────────────────────────────────

export const fetchAssessmentYears = async (is_active) => {
 const params = {};
 if (is_active !== undefined && is_active !== null) params.is_active = is_active;
 const response = await apiClient.get(`/master/commons/assessment-years`, { params });
 return response.data;
};

export const fetchAssessmentYearById = async (id) => {
 const response = await apiClient.get(`/master/commons/assessment-years/${id}`);
 return response.data;
};

export const createAssessmentYear = async (data) => {
 const response = await apiClient.post(`/master/commons/assessment-years`, data);
 return response.data;
};

export const updateAssessmentYear = async (id, data) => {
 const response = await apiClient.put(`/master/commons/assessment-years/${id}`, data);
 return response.data;
};

export const deleteAssessmentYear = async (id) => {
 const response = await apiClient.delete(`/master/commons/assessment-years/${id}`);
 return response.data;
};


// ── Holidays ─────────────────────────────────────────────────────────────

export const fetchHolidays = async ({ assessment_year_id, state_id, page = 1, page_size = 50 } = {}) => {
 const params = { page, page_size };
 if (assessment_year_id) params.assessment_year_id = assessment_year_id;
 if (state_id) params.state_id = state_id;
 const response = await apiClient.get(`/master/commons/holidays`, { params });
 return response.data;
};

export const fetchHolidaysCalendar = async (assessment_year_id, month, year, state_id) => {
 const params = { assessment_year_id, month, year };
 if (state_id) params.state_id = state_id;
 const response = await apiClient.get(`/master/commons/holidays/calendar`, { params });
 return response.data;
};

// A "holiday group" is a (state, assessment year) pair — Holidays.js groups
// individual holiday rows into one client-side. There's no single-id lookup
// for a group; reuse the existing filterable list endpoint (it already
// supports both filters) and reshape into the same group shape the page
// builds itself, so the edit form can reopen after a refresh.
export const fetchHolidayGroupById = async (stateId, assessmentYearId) => {
 const response = await apiClient.get(`/master/commons/holidays`, {
  params: { state_id: stateId, assessment_year_id: assessmentYearId, page_size: 200 },
 });
 return {
  success: response.data.success,
  data: {
   state_id: Number(stateId),
   assessment_year_id: assessmentYearId,
   holidays: response.data.data || [],
  },
 };
};

export const createHoliday = async (data) => {
 const response = await apiClient.post(`/master/commons/holidays`, data);
 return response.data;
};

export const updateHoliday = async (id, data) => {
 const response = await apiClient.put(`/master/commons/holidays/${id}`, data);
 return response.data;
};

// Saves a whole (state, year) calendar in one all-or-nothing request. original_* are null
// for a new calendar.
export const saveHolidayCalendar = async (payload) => {
 const response = await apiClient.put(`/master/commons/holiday-calendars`, payload);
 return response.data;
};

export const deleteHoliday = async (id) => {
 const response = await apiClient.delete(`/master/commons/holidays/${id}`);
 return response.data;
};


// ── Cycles ───────────────────────────────────────────────────────────────

export const fetchCycles = async ({ state_id, cycle_type, assessment_year_id, status, search, page = 1, page_size = 20 } = {}) => {
 const params = { page, page_size };
 if (state_id) params.state_id = state_id;
 if (cycle_type) params.cycle_type = cycle_type;
 if (assessment_year_id) params.assessment_year_id = assessment_year_id;
 if (status) params.status = status;
 if (search) params.search = search;
 const response = await apiClient.get(`/master/commons/cycles`, { params });
 return response.data;
};

export const fetchCycle = async (id) => {
 const response = await apiClient.get(`/master/commons/cycles/${id}`);
 return response.data;
};

export const fetchCycleSchools = async (id, { page = 1, page_size = 20, search = "" } = {}) => {
 const response = await apiClient.get(`/master/commons/cycles/${id}/schools`, {
  params: { page, page_size, search },
 });
 return response.data;
};

// Just the mapped school_ids, unpaginated. The quarterly edit form needs the
// full set because updateCycle still replaces it wholesale.
export const fetchCycleSchoolIds = async (id) => {
 const response = await apiClient.get(`/master/commons/cycles/${id}/schools`, {
  params: { ids_only: true },
 });
 return response.data;
};

export const createCycle = async (data) => {
 const response = await apiClient.post(`/master/commons/cycles`, data);
 return response.data;
};

export const updateCycle = async (id, data) => {
 const response = await apiClient.put(`/master/commons/cycles/${id}`, data);
 return response.data;
};

export const publishCycle = async (id) => {
 const response = await apiClient.patch(`/master/commons/cycles/${id}/publish`);
 return response.data;
};

export const unpublishCycle = async (id) => {
 const response = await apiClient.patch(`/master/commons/cycles/${id}/unpublish`);
 return response.data;
};

export const completeCycle = async (id) => {
 const response = await apiClient.patch(`/master/commons/cycles/${id}/complete`);
 return response.data;
};

export const deleteCycle = async (id) => {
 const response = await apiClient.delete(`/master/commons/cycles/${id}`);
 return response.data;
};

// ── Cycle Reminders ──────────────────────────────────────────────────────

export const addCycleReminder = async (cycleId, data) => {
 const response = await apiClient.post(`/master/commons/cycles/${cycleId}/reminders`, data);
 return response.data;
};

export const listCycleReminders = async (cycleId) => {
 const response = await apiClient.get(`/master/commons/cycles/${cycleId}/reminders`);
 return response.data;
};

export const updateCycleReminder = async (cycleId, reminderId, data) => {
 const response = await apiClient.put(`/master/commons/cycles/${cycleId}/reminders/${reminderId}`, data);
 return response.data;
};

export const deleteCycleReminder = async (cycleId, reminderId) => {
 const response = await apiClient.delete(`/master/commons/cycles/${cycleId}/reminders/${reminderId}`);
 return response.data;
};


// ── Competency (Neo4j) ───────────────────────────────────────────────────

export const fetchCompetencyConfig = async () => {
 const response = await apiClient.get(`/master/competencies/config`);
 return response.data;
};

export const fetchCompetenciesByGrade = async (grade, stateId) => {
 const params = { state_id: stateId };
 const response = await apiClient.get(`/master/competencies/by-grade/${encodeURIComponent(grade)}`, { params });
 return response.data;
};

// Returns { [competency_code]: { category_name, weight } } for a state.
export const fetchCompetencyCategoryMap = async (stateId) => {
 const response = await apiClient.get(`/master/commons/competency-category-map`, { params: { state_id: stateId } });
 return response.data;
};

export const fetchCompetencies = async ({ grade, subject, category, state_id, search = "", page = 1, page_size = 10, include_deleted, deleted_only } = {}) => {
 const params = { page, page_size, search };
 if (state_id) params.state_id = state_id;
 if (grade) params.grade = grade;
 if (subject) params.subject = subject;
 if (category) params.category = category;
 if (include_deleted !== undefined && include_deleted !== null && include_deleted !== "") params.include_deleted = include_deleted;
 if (deleted_only !== undefined && deleted_only !== null && deleted_only !== "") params.deleted_only = deleted_only;
 const response = await apiClient.get(`/master/competencies/`, { params });
 return response.data;
};

export const fetchCompetency = async (code, stateId) => {
 const params = stateId ? { state_id: stateId } : {};
 const response = await apiClient.get(`/master/competencies/${encodeURIComponent(code)}`, { params });
 return response.data;
};

export const createCompetency = async (data) => {
 const response = await apiClient.post(`/master/competencies/`, data);
 return response.data;
};

export const updateCompetency = async (code, data, stateId) => {
 const params = {};
 // Only include state_id if it's not null (for legacy competencies)
 if (stateId !== null && stateId !== undefined) {
   params.state_id = stateId;
 }
 const response = await apiClient.put(`/master/competencies/${encodeURIComponent(code)}`, data, { params });
 return response.data;
};

export const deleteCompetency = async (code, stateId) => {
 const params = {};
 // Only include state_id if it's not null (for legacy competencies)
 if (stateId !== null && stateId !== undefined) {
   params.state_id = stateId;
 }
 const response = await apiClient.delete(`/master/competencies/${encodeURIComponent(code)}`, { params });
 return response.data;
};

// Restore a soft-deleted competency (requires SPL+ write role, enforced server-side)
export const restoreCompetency = async (code, stateId) => {
 const params = {};
 if (stateId !== null && stateId !== undefined) {
   params.state_id = stateId;
 }
 const response = await apiClient.post(`/master/competencies/${encodeURIComponent(code)}/restore`, null, { params });
 return response.data;
};

export const fetchCompetencyTree = async (code) => {
 const response = await apiClient.get(`/master/competencies/${encodeURIComponent(code)}/tree`);
 return response.data;
};

// categories (full CRUD)
export const fetchCompetencyCategories = async ({ page = 1, page_size = 10, search = "" } = {}) => {
 const params = { page, page_size, search };
 const response = await apiClient.get(`/master/competencies/categories`, { params });
 return response.data;
};

export const fetchCompetencyCategoryById = async (id) => {
 const response = await apiClient.get(`/master/competencies/categories/${encodeURIComponent(id)}`);
 return response.data;
};

export const createCompetencyCategory = async (data) => {
 const response = await apiClient.post(`/master/competencies/categories`, data);
 return response.data;
};

export const updateCompetencyCategory = async (id, data) => {
 const response = await apiClient.put(`/master/competencies/categories/${encodeURIComponent(id)}`, data);
 return response.data;
};

export const deleteCompetencyCategory = async (id) => {
 const response = await apiClient.delete(`/master/competencies/categories/${encodeURIComponent(id)}`);
 return response.data;
};

// headers (full CRUD)
export const fetchCompetencyHeaders = async ({ page = 1, page_size = 10, search = "" } = {}) => {
 const params = { page, page_size, search };
 const response = await apiClient.get(`/master/competencies/headers`, { params });
 return response.data;
};

export const createCompetencyHeader = async (data) => {
 const response = await apiClient.post(`/master/competencies/headers`, data);
 return response.data;
};

export const updateCompetencyHeader = async (id, data) => {
 const response = await apiClient.put(`/master/competencies/headers/${encodeURIComponent(id)}`, data);
 return response.data;
};

export const deleteCompetencyHeader = async (id) => {
 const response = await apiClient.delete(`/master/competencies/headers/${encodeURIComponent(id)}`);
 return response.data;
};

export const fetchCompetencyHeaderDetail = async (id) => {
 const response = await apiClient.get(`/master/competencies/headers/${encodeURIComponent(id)}`);
 return response.data;
};

// sub-headers (full CRUD)
export const fetchCompetencySubHeaders = async (headerId) => {
 const response = await apiClient.get(`/master/competencies/sub-headers/${encodeURIComponent(headerId)}`);
 return response.data;
};

export const createCompetencySubHeader = async (data) => {
 const response = await apiClient.post(`/master/competencies/sub-headers`, data);
 return response.data;
};

export const updateCompetencySubHeader = async (id, data) => {
 const response = await apiClient.put(`/master/competencies/sub-headers/${encodeURIComponent(id)}`, data);
 return response.data;
};

export const deleteCompetencySubHeader = async (id) => {
 const response = await apiClient.delete(`/master/competencies/sub-headers/${encodeURIComponent(id)}`);
 return response.data;
};


// ── Student Categories (per cycle — master data service) ─────────────

export const listStudentCategories = async (cycleId) => {
 const response = await apiClient.get(`/master/commons/student-category/${cycleId}/categories`);
 return response.data;
};

export const createStudentCategory = async (cycleId, data) => {
 const response = await apiClient.post(`/master/commons/student-category/${cycleId}/categories`, data);
 return response.data;
};

export const updateStudentCategory = async (cycleId, categoryId, data) => {
 const response = await apiClient.put(`/master/commons/student-category/${cycleId}/categories/${categoryId}`, data);
 return response.data;
};

export const deleteStudentCategory = async (cycleId, categoryId) => {
 const response = await apiClient.delete(`/master/commons/student-category/${cycleId}/categories/${categoryId}`);
 return response.data;
};

export const fetchStudentsByCategory = async (cycleId, { grade } = {}) => {
 const params = {};
 if (grade) params.grade = grade;
 const response = await apiClient.get(`/master/commons/student-category/${cycleId}/students`, { params });
 return response.data;
};

export const exportStudentsCategoryCsv = async (cycleId, { grade } = {}) => {
 const params = {};
 if (grade) params.grade = grade;
 const response = await apiClient.get(`/master/commons/student-category/${cycleId}/students/export`, {
  params,
  responseType: "blob",
 });
 return response.data;
};

export const fetchPreviousRoundStudents = async ({ state_id, previous_round, cycle_type = "quarterly", grade, weightOnly } = {}) => {
 const params = { state_id, previous_round, cycle_type };
 if (grade) params.grade = grade;
 if (weightOnly) params.weight_only = true;
 const response = await apiClient.get(`/master/commons/previous-round-students`, { params });
 return response.data;
};


// ── Competency Sets (Neo4j) ────────────────────────────────────────────────

export const fetchAllSets = async ({ page = 1, page_size = 10, search = "", state_id, competency_code, set_type, include_deleted, deleted_only } = {}) => {
 const params = { page, page_size, search };
 if (state_id) params.state_id = state_id;
 if (competency_code) params.competency_code = competency_code;
 if (set_type) params.set_type = set_type;
 if (include_deleted !== undefined && include_deleted !== null && include_deleted !== "") params.include_deleted = include_deleted;
 if (deleted_only !== undefined && deleted_only !== null && deleted_only !== "") params.deleted_only = deleted_only;
 const response = await apiClient.get(`/master/competency-sets/all`, { params });
 return response.data;
};

// Lightweight, unpaginated list of all accessible sets: [{ id, title, competency_code }]
export const fetchAllSetsLite = async () => {
 const response = await apiClient.get(`/master/competency-sets/all/lite`);
 return response.data;
};

export const fetchSetsForCompetency = async (competencyCode) => {
 const response = await apiClient.get(`/master/competency-sets/by-competency/${encodeURIComponent(competencyCode)}`);
 return response.data;
};

export const fetchSet = async (setId) => {
 const response = await apiClient.get(`/master/competency-sets/${encodeURIComponent(setId)}`);
 return response.data;
};

export const createSet = async (data) => {
 const response = await apiClient.post(`/master/competency-sets/`, data);
 return response.data;
};

export const updateSet = async (setId, data) => {
 const response = await apiClient.put(`/master/competency-sets/${encodeURIComponent(setId)}`, data);
 return response.data;
};

export const deleteSet = async (setId) => {
 const response = await apiClient.delete(`/master/competency-sets/${encodeURIComponent(setId)}`);
 return response.data;
};

// Restore a soft-deleted competency set (requires SPL+ write role, enforced server-side)
export const restoreSet = async (setId) => {
 const response = await apiClient.post(`/master/competency-sets/${encodeURIComponent(setId)}/restore`);
 return response.data;
};

// Fetch sets from dependency competencies (for cross-set dependency dropdown)
export const fetchDependencySets = async (competencyCode) => {
 const response = await apiClient.get(`/master/competency-sets/dependency-sets/${encodeURIComponent(competencyCode)}`);
 return response.data;
};

// Get next questions filtered by grid position
export const fetchNextQuestions = async (setId, studentId) => {
 const response = await apiClient.get(`/master/competency-sets/next-questions/${encodeURIComponent(setId)}/${encodeURIComponent(studentId)}`);
 return response.data;
};

// ── Questions (under Sets) ──────────────────────────────────────────────────

export const fetchQuestionsForSet = async (setId) => {
 const response = await apiClient.get(`/master/competency-sets/questions/by-set/${encodeURIComponent(setId)}`);
 return response.data;
};

export const fetchQuestion = async (questionId) => {
 const response = await apiClient.get(`/master/competency-sets/questions/${encodeURIComponent(questionId)}`);
 return response.data;
};

export const createQuestion = async (data) => {
 const response = await apiClient.post(`/master/competency-sets/questions`, data);
 return response.data;
};

export const updateQuestion = async (questionId, data) => {
 const response = await apiClient.put(`/master/competency-sets/questions/${encodeURIComponent(questionId)}`, data);
 return response.data;
};

export const deleteQuestion = async (questionId) => {
 const response = await apiClient.delete(`/master/competency-sets/questions/${encodeURIComponent(questionId)}`);
 return response.data;
};

// Upload a resource file (image) for competency sets
export const uploadSetResource = async (file) => {
 const formData = new FormData();
 formData.append("file", file);
 const response = await apiClient.post(`/master/competency-sets/upload-resource`, formData, {
   headers: { "Content-Type": "multipart/form-data" },
 });
 return response.data;
};

// ── Sync Logs (Admin) ──────────────────────────────────────────────────

export const fetchSyncLogs = async ({ status, sync_type, school_id, search, page = 1, page_size = 50 } = {}) => {
 const params = { page, page_size };
 if (status) params.status = status;
 if (sync_type) params.sync_type = sync_type;
 if (school_id) params.school_id = school_id;
 if (search) params.search = search;
 const response = await apiClient.get(`/field-execution/admin/sync-logs`, { params });
 return response.data;
};

export const fetchSyncLogsSummary = async () => {
 const response = await apiClient.get(`/field-execution/admin/sync-logs/summary`);
 return response.data;
};
